/*
 * Meta Ads landing page ("Not another chatbot"), paired with sections/lp-not-a-chatbot.liquid.
 *
 * Primary goal is a booked call. Call CTAs open the Cal.com popup on the page, so the booking
 * itself is tracked; without the popup (or if Cal.com fails to load) they link to the booking page.
 *
 * Conversion events (all carry hook, hook_source, utm_* and page path):
 *   lp_view         once per page load
 *   lp_cta_click    any [data-lp-cta] click; cta = call | install, placement = hero | offer | sticky
 *   lp_call_booked  Cal.com bookingSuccessfulV2 in the popup  <- the conversion
 *   lp_offer_view   offer block scrolled into view
 *   lp_faq_open     FAQ item opened
 *
 * Destinations:
 *   PostHog        posthog.capture(<event>)
 *   Shopify        Shopify.analytics.publish('shopguide_<event>'), so a Customer Events custom pixel
 *                  can forward installs to Meta (the Facebook channel pixel runs sandboxed, no window.fbq)
 *   Meta Pixel     window.fbq if present on the page: call click = Lead, booked call = Schedule
 *   dataLayer      for GTM, if present
 */
(function () {
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'hook'];

  function init(root) {
    if (root.dataset.lpReady) return;
    root.dataset.lpReady = 'true';
    root.classList.add('lp-nc--js');

    var designMode = root.dataset.designMode === 'true' || (window.Shopify && window.Shopify.designMode);
    var params = new URLSearchParams(window.location.search);
    var context = {
      hook: root.dataset.hook,
      hook_source: root.dataset.hookSource || 'default',
      page_path: window.location.pathname,
    };
    UTM_KEYS.forEach(function (k) {
      if (k !== 'hook' && params.get(k)) context[k] = params.get(k);
    });

    function track(event, props) {
      if (designMode) return;
      var payload = Object.assign({}, context, props || {});
      try {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture(event, payload, { transport: 'sendBeacon' });
        }
      } catch (e) {}
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('shopguide_' + event, payload);
        }
      } catch (e) {}
      try {
        if (typeof window.fbq === 'function') {
          if (event === 'lp_cta_click' && payload.cta === 'call') {
            window.fbq('track', 'Lead', { content_name: 'shopguide_call_click', content_category: payload.hook });
          } else if (event === 'lp_call_booked') {
            window.fbq('track', 'Schedule', { content_name: 'shopguide_call_booked', content_category: payload.hook });
          }
        }
      } catch (e) {}
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: event }, payload));
    }

    // Carry the ad's UTMs through to the App Store listing so installs can be attributed there too.
    root.querySelectorAll('[data-lp-cta="install"]').forEach(function (link) {
      try {
        var url = new URL(link.href);
        UTM_KEYS.forEach(function (k) {
          var v = k === 'hook' ? context.hook : context[k];
          if (v && !url.searchParams.has(k)) url.searchParams.set(k, v);
        });
        link.href = url.toString();
      } catch (e) {}
    });

    // Same attribution for the call: UTMs on the fallback link and in the popup's booking URL.
    var calParams = {};
    UTM_KEYS.forEach(function (k) {
      var v = k === 'hook' ? context.hook : context[k];
      if (v) calParams[k === 'hook' ? 'metadata[hook]' : k] = v;
    });
    root.querySelectorAll('[data-lp-cta="call"]').forEach(function (link) {
      try {
        var url = new URL(link.href);
        Object.keys(calParams).forEach(function (k) {
          if (!url.searchParams.has(k)) url.searchParams.set(k, calParams[k]);
        });
        link.href = url.toString();
      } catch (e) {}
    });
    if (!designMode && root.dataset.calPopup === 'true' && root.dataset.calLink) {
      loadCal(function (payload) {
        track('lp_call_booked', payload);
      });
    }

    root.addEventListener('click', function (event) {
      var cta = event.target.closest('[data-lp-cta]');
      if (!cta) return;
      if (cta.dataset.lpCta === 'call' && calReady) {
        event.preventDefault();
        window.Cal.ns.lp('modal', {
          calLink: root.dataset.calLink,
          config: Object.assign({ layout: 'month_view' }, calParams),
        });
      }
      track('lp_cta_click', {
        cta: cta.dataset.lpCta,
        placement: cta.dataset.lpPlacement,
        destination: cta.href,
      });
    });

    root.querySelectorAll('[data-lp-faq]').forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (item.open) track('lp_faq_open', { question: item.dataset.lpFaq });
      });
    });

    highlightContrast(root, context.hook);
    playMock(root, designMode);

    if ('IntersectionObserver' in window) {
      var reveal = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            reveal.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      root.querySelectorAll('.lp-reveal').forEach(function (el) {
        reveal.observe(el);
      });

      var offer = root.querySelector('.lp-offer');
      if (offer) {
        var offerSeen = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            track('lp_offer_view');
            offerSeen.disconnect();
          },
          { threshold: 0.4 }
        );
        offerSeen.observe(offer);
      }

      var sticky = root.querySelector('[data-lp-sticky]');
      var hero = root.querySelector('.lp-hero__actions');
      if (sticky && hero) {
        // Visible once the hero buttons are scrolled past, hidden while the offer (its own CTA) is on screen.
        var pastHero = false;
        var offerVisible = false;
        sticky.hidden = false;
        var watch = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.target === hero) pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            if (entry.target === offer) offerVisible = entry.isIntersecting;
          });
          sticky.classList.toggle('is-visible', pastHero && !offerVisible);
        });
        watch.observe(hero);
        if (offer) watch.observe(offer);
      }
    } else {
      root.querySelectorAll('.lp-reveal').forEach(function (el) {
        el.classList.add('is-in');
      });
    }

    track('lp_view');
  }

  // Cal.com's standard embed loader, namespaced "lp". Call links only open the popup once
  // embed.js has actually loaded; until then (or if it's blocked) they open the booking page.
  var calLoaded = false;
  var calReady = false;
  function loadCal(onBooked) {
    if (calLoaded) return;
    calLoaded = true;
    (function (C, A, L) {
      var p = function (a, ar) { a.q.push(ar); };
      var d = C.document;
      C.Cal = C.Cal || function () {
        var cal = C.Cal;
        var ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement('script')).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          var api = function () { p(api, arguments); };
          var namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === 'string') {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ['initNamespace', namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, 'https://app.cal.com/embed/embed.js', 'init');

    window.Cal('init', 'lp', { origin: 'https://cal.com' });
    // The loader injects embed.js on the first Cal() call above.
    var script = document.querySelector('script[src="https://app.cal.com/embed/embed.js"]');
    if (script) {
      script.addEventListener('load', function () {
        calReady = true;
      });
    }
    window.Cal.ns.lp('ui', { hideEventTypeDetails: false, layout: 'month_view' });
    window.Cal.ns.lp('on', {
      action: 'bookingSuccessfulV2',
      callback: function (e) {
        var data = (e && e.detail && e.detail.data) || {};
        onBooked({ booking_start: data.startTime, event_type_id: data.eventTypeId });
      },
    });
  }

  // Put the contrast row that matches the clicked ad first, so the story continues.
  function highlightContrast(root, hook) {
    var list = root.querySelector('.lp-contrast');
    if (!list || !hook) return;
    list.querySelectorAll('[data-contrast-key]').forEach(function (row) {
      row.removeAttribute('data-current');
    });
    var match = list.querySelector('[data-contrast-key="' + CSS.escape(hook) + '"]');
    if (!match) return;
    match.setAttribute('data-current', '');
    list.prepend(match);
  }

  function playMock(root, designMode) {
    var mock = root.querySelector('.lp-mock');
    if (!mock || designMode || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    mock.classList.add('lp-mock--typing');
    setTimeout(function () {
      mock.classList.remove('lp-mock--typing');
    }, 2900);
  }

  function setHook(root, key) {
    root.dataset.hook = key;
    root.querySelectorAll('[data-hook-key]').forEach(function (el) {
      el.toggleAttribute('data-active', el.dataset.hookKey === key);
    });
    highlightContrast(root, key);
  }

  function boot() {
    document.querySelectorAll('[data-lp-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init re-rendered sections, preview the selected hook block.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-lp-root]');
    if (root) init(root);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var hook = event.target.closest('[data-hook-key]');
    var root = event.target.closest('[data-lp-root]');
    if (hook && root) setHook(root, hook.dataset.hookKey);
  });
})();
